import { FC, FormEvent, useContext } from 'react';

import './Payment.css';
import { AuthContext } from '../../api/authContext';
import { Button, Dialog, Meta, Notice, SectionHeader, Stack } from '../../ui';
import CustomerPaymentPreview from './payment/CustomerPaymentPreview';
import PayNowSettingsCard from './payment/PayNowSettingsCard';
import QrAppearanceCard from './payment/QrAppearanceCard';
import RemovePaynowSection from './payment/RemovePaynowSection';
import { usePaynowPreview } from './payment/usePaynowPreview';
import { usePaynowSettings } from './payment/usePaynowSettings';

type PaymentProps = {
  onUnauthorized: () => void;
};

/**
 * The booth artist's PayNow details and QR appearance.
 *
 * The form sits beside a live preview of the exact code the customer display
 * will draw, so the look can be judged before saving. Payment is always
 * embedded by SettingsPage, so it renders a section header - GroupPage owns the
 * page's h1 and the "back to till" control.
 */
const Payment: FC<PaymentProps> = ({ onUnauthorized }) => {
  const { username } = useContext(AuthContext);
  const {
    form,
    setField,
    logo,
    configured,
    loading,
    saving,
    uploading,
    error,
    notice,
    confirmRemove,
    setConfirmRemove,
    dirty,
    chooseLogo,
    removeLogo,
    savePaymentSettings,
    removePaynow,
    dismissError,
  } = usePaynowSettings(onUnauthorized);
  const { state: previewState } = usePaynowPreview(form, loading);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    savePaymentSettings();
  };

  const valueEmpty = form.value.trim() === '';
  // PayNow is account-scoped (/api/account/paynow), so removing it names the
  // account, never the currently selected booth event.
  const removeTarget = username ?? 'this account';

  // paymentCard, paymentDanger, paymentLogoThumb, paymentLogoEmpty and the
  // paymentPreview* names are the e2e contract. paymentCard and paymentDanger
  // carry no styling; the paymentLogo* and paymentPreview* names do.
  return (
    <div>
      <SectionHeader title="Payment" description="Where customer PayNow payments go, and how the code looks." />

      {error && (
        <Notice variant="error" onDismiss={dismissError}>
          {error}
        </Notice>
      )}
      {notice && <Notice variant="success">{notice}</Notice>}

      {loading ? (
        <Meta role="status">Loading…</Meta>
      ) : (
        <div className="paymentBody">
          <Stack gap={6}>
            <form onSubmit={handleSubmit}>
              <Stack gap={6}>
                <PayNowSettingsCard form={form} setField={setField} username={username} />
                <QrAppearanceCard
                  form={form}
                  setField={setField}
                  logo={logo}
                  uploading={uploading}
                  onChoose={chooseLogo}
                  onRemove={removeLogo}
                />

                <Stack direction="row" gap={5}>
                  <Button type="submit" variant="primary" disabled={saving || uploading || !dirty || valueEmpty}>
                    {saving ? 'Saving…' : 'Save changes'}
                  </Button>
                  {dirty && <Meta as="span">Unsaved changes</Meta>}
                </Stack>

                {valueEmpty && dirty && (
                  <Notice variant="warning">Enter the number or UEN that should receive the payment before saving.</Notice>
                )}
              </Stack>
            </form>

            {configured && <RemovePaynowSection saving={saving} onRemove={() => setConfirmRemove(true)} />}
          </Stack>

          <CustomerPaymentPreview state={previewState} />
        </div>
      )}

      <Dialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={`Remove PayNow from ${removeTarget}?`}
        description="Customers will no longer be able to pay using this PayNow destination."
        tone="danger"
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={removePaynow} disabled={saving}>
              {saving ? 'Removing…' : 'Remove PayNow'}
            </Button>
          </>
        }
      />
    </div>
  );
};

export default Payment;
